/**
 * Manual quote schedule times: backend {@code LocalTime} via Jackson
 * {@code @JsonFormat(shape = STRING, pattern = "h:mm a")} — e.g. {@code "1:00 PM"}.
 */

const MINUTES_PER_DAY = 24 * 60;

/**
 * @param {string|null|undefined} timeStr
 * @returns {number|null} minutes 0–1439 from local midnight, or null
 */
export function parseFlexibleTimeToMinutes(timeStr) {
  if (timeStr == null || String(timeStr).trim() === "") return null;
  const s0 = String(timeStr).trim();
  const noFrac = s0.split(".")[0].trim();
  const ampmMatch = noFrac.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s*$/i);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const mi = parseInt(ampmMatch[2], 10);
    const ap = ampmMatch[4].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(mi) || mi < 0 || mi > 59) return null;
    if (h < 1 || h > 12) return null;
    if (h === 12) h = 0;
    if (ap === "PM") h += 12;
    const total = h * 60 + mi;
    return total >= 0 && total < MINUTES_PER_DAY ? total : null;
  }
  const parts = noFrac.split(":").map((p) => p.trim());
  if (parts.length < 2) return null;
  const hi = parseInt(parts[0], 10);
  const mi = parseInt(parts[1], 10);
  const si = parts.length >= 3 ? parseInt(parts[2], 10) : 0;
  if (Number.isNaN(hi) || Number.isNaN(mi) || Number.isNaN(si)) return null;
  if (hi < 0 || hi > 23 || mi < 0 || mi > 59 || si < 0 || si > 59) return null;
  return hi * 60 + mi;
}

/**
 * @param {number} totalMinutes
 * @param {boolean} includeSeconds
 */
function formatMinutesAsHhMm(totalMinutes, includeSeconds) {
  if (totalMinutes < 0 || totalMinutes >= MINUTES_PER_DAY) return null;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return includeSeconds ? `${hh}:${mm}:00` : `${hh}:${mm}`;
}

/**
 * When the backend sends same-calendar-day slots in 24h form, an afternoon end can appear as
 * "01:00:00" (1 AM) instead of "13:00:00" (1 PM). If start is 11 AM or later and end parses as
 * before noon and strictly before start, add 12 hours to end once.
 *
 * @param {string|null|undefined} startRaw
 * @param {string|null|undefined} endRaw
 * @returns {{ startStr: string, endStr: string, validOrder: boolean }}
 */
export function coerceManualQuoteSchedulePair(startRaw, endRaw) {
  const startEmpty = startRaw == null || String(startRaw).trim() === "";
  const endEmpty = endRaw == null || String(endRaw).trim() === "";
  if (startEmpty || endEmpty) {
    return {
      startStr: startEmpty ? "" : String(startRaw).trim(),
      endStr: endEmpty ? "" : String(endRaw).trim(),
      validOrder: false
    };
  }
  const sm = parseFlexibleTimeToMinutes(startRaw);
  const em = parseFlexibleTimeToMinutes(endRaw);
  if (sm === null || em === null) {
    return {
      startStr: String(startRaw).trim(),
      endStr: String(endRaw).trim(),
      validOrder: false
    };
  }
  let e = em;
  const s = sm;
  const shouldBumpAfternoonEnd =
    e < s && e < 12 * 60 && s >= 11 * 60;
  if (shouldBumpAfternoonEnd) {
    e += 12 * 60;
  }
  const includeSeconds =
    String(startRaw).trim().split(":").length >= 3 ||
    String(endRaw).trim().split(":").length >= 3;
  const validOrder = e > s;
  const startStr = formatMinutesAsHhMm(s, includeSeconds) ?? String(startRaw).trim();
  const endStr = formatMinutesAsHhMm(e, includeSeconds) ?? String(endRaw).trim();
  return { startStr, endStr, validOrder };
}

/**
 * Normalize one availability slot (public quote, job reschedule, etc.): same afternoon end-time
 * coercion as {@link coerceManualQuoteSchedulePair}. Sets startTime/endTime and start/end.
 */
export function normalizeAvailableSlotTimes(slot) {
  if (!slot || typeof slot !== "object") return slot;
  const st = slot.startTime ?? slot.start;
  const en = slot.endTime ?? slot.end;
  if (st == null || en == null) return slot;
  if (String(st).trim() === "" || String(en).trim() === "") return slot;
  const { startStr, endStr } = coerceManualQuoteSchedulePair(st, en);
  return {
    ...slot,
    startTime: startStr,
    endTime: endStr,
    start: startStr,
    end: endStr
  };
}

/** @param {unknown} slots */
export function normalizeAvailableSlotsList(slots) {
  if (!Array.isArray(slots)) return [];
  return slots.map((s) => normalizeAvailableSlotTimes(s));
}

/**
 * True when end is strictly after start on the same calendar day (after optional afternoon coercion).
 */
export function isManualQuoteScheduleOrderValid(startRaw, endRaw) {
  return coerceManualQuoteSchedulePair(startRaw, endRaw).validOrder;
}

/**
 * Both times are non-empty and parse as clock times, but end is not after start (even after afternoon coercion).
 */
export function hasInvalidSameDayTimeOrderWhenNonEmpty(startRaw, endRaw) {
  if (startRaw == null || endRaw == null) return false;
  if (String(startRaw).trim() === "" || String(endRaw).trim() === "") return false;
  if (parseFlexibleTimeToMinutes(startRaw) == null || parseFlexibleTimeToMinutes(endRaw) == null) {
    return false;
  }
  return !coerceManualQuoteSchedulePair(startRaw, endRaw).validOrder;
}

/**
 * Fix employee available-slot payloads for UI + submit (same coercion as {@link normalizeManualQuoteScheduleTimes}).
 */
export function normalizeEmployeeScheduleSlotTimes(startRaw, endRaw) {
  return coerceManualQuoteSchedulePair(startRaw, endRaw);
}

/**
 * ISO-style {@code HH:mm:ss} for APIs that use {@code LocalTime} without a custom Jackson pattern.
 *
 * @param {string|null|undefined} timeStr - e.g. "9:00", "09:00:00", "14:30:45"
 * @returns {string} "" if empty/invalid; otherwise zero-padded {@code HH:mm:ss}
 */
export function toJavaLocalTimeString(timeStr) {
  const m = parseFlexibleTimeToMinutes(timeStr);
  if (m === null) return "";
  return formatMinutesAsHhMm(m, true) ?? "";
}

/**
 * Matches {@code ManualQuoteScheduleRequest} Jackson format:
 * {@code @JsonFormat(shape = STRING, pattern = "h:mm a")} → e.g. {@code "1:00 AM"}, {@code "5:30 PM"}.
 *
 * @param {string|null|undefined} timeStr - 24h {@code HH:mm} / {@code HH:mm:ss}, or already {@code h:mm a}
 * @returns {string} "" if empty/invalid
 */
export function toManualQuoteScheduleLocalTimeString(timeStr) {
  if (timeStr == null || String(timeStr).trim() === "") return "";
  const s0 = String(timeStr).trim();
  if (/\b(AM|PM)\b/i.test(s0)) {
    const m = parseFlexibleTimeToMinutes(s0);
    if (m === null) return "";
    const hi = Math.floor(m / 60);
    const mi = m % 60;
    const isAm = hi < 12;
    let h12 = hi % 12;
    if (h12 === 0) h12 = 12;
    const mm = String(mi).padStart(2, "0");
    const suffix = isAm ? "AM" : "PM";
    return `${h12}:${mm} ${suffix}`;
  }
  const m = parseFlexibleTimeToMinutes(s0);
  if (m === null) return "";
  const hi = Math.floor(m / 60);
  const mi = m % 60;
  const isAm = hi < 12;
  let h12 = hi % 12;
  if (h12 === 0) h12 = 12;
  const mm = String(mi).padStart(2, "0");
  const suffix = isAm ? "AM" : "PM";
  return `${h12}:${mm} ${suffix}`;
}

/**
 * Same {@code startTime} / {@code endTime} for manual quote {@code schedule} (scheduled or completed):
 * strings in {@code h:mm a} form for {@code ManualQuoteScheduleRequest}.
 * Applies {@link coerceManualQuoteSchedulePair} so noon→1 PM slots are not sent as 12 PM→1 AM.
 *
 * @param {string|null|undefined} startRaw
 * @param {string|null|undefined} endRaw
 */
export function normalizeManualQuoteScheduleTimes(startRaw, endRaw) {
  const { startStr, endStr } = coerceManualQuoteSchedulePair(startRaw, endRaw);
  return {
    startTime: toManualQuoteScheduleLocalTimeString(startStr),
    endTime: toManualQuoteScheduleLocalTimeString(endStr)
  };
}

/**
 * Display-only range for quote / booking UI, e.g. {@code "9:00 AM – 10:00 AM"}.
 * Does not change values used for Select {@code value} or API payloads.
 */
export function formatQuoteScheduleRangeDisplay(startRaw, endRaw) {
  const sDisp =
    startRaw != null && String(startRaw).trim() !== ""
      ? toManualQuoteScheduleLocalTimeString(String(startRaw)) || String(startRaw).trim()
      : "";
  const eDisp =
    endRaw != null && String(endRaw).trim() !== ""
      ? toManualQuoteScheduleLocalTimeString(String(endRaw)) || String(endRaw).trim()
      : "";
  if (sDisp && eDisp) return `${sDisp} – ${eDisp}`;
  return sDisp || eDisp || "";
}

/** Same as {@link formatQuoteScheduleRangeDisplay} but phrasing for slot dropdowns. */
export function formatQuoteScheduleSlotBetweenLabel(startRaw, endRaw) {
  const sDisp =
    startRaw != null && String(startRaw).trim() !== ""
      ? toManualQuoteScheduleLocalTimeString(String(startRaw)) || String(startRaw).trim()
      : "";
  const eDisp =
    endRaw != null && String(endRaw).trim() !== ""
      ? toManualQuoteScheduleLocalTimeString(String(endRaw)) || String(endRaw).trim()
      : "";
  if (sDisp && eDisp) return `between ${sDisp} - ${eDisp}`;
  if (sDisp) return sDisp;
  return eDisp || "";
}
